import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAutoSave } from '../../../../hooks/useAutoSave';
import type { AutoSaveStatus } from '../../../../hooks/useAutoSave';
import TabNav from '../../../../composants/TabNav/TabNav';
import Button from '../../../../composants/Button/Button';
import ValidationPopUp from '../../../../composants/ValidationPopUp/ValidationPopUp';
import CandidatureHeader from './CandidatureHeader';
import SectionCandidat from './SectionCandidat';
import SectionChoixLots from './SectionChoixLots';
import SectionOffreTechnique, { CRITERES_TECHNIQUES } from './SectionOffreTechnique';
import SectionOffreFinanciere from './SectionOffreFinanciere';
import type { Article, PrixLigne, RegionGroup } from './SectionOffreFinanciere';
import SectionSoumission from './SectionSoumission';
import type { ValidationItem } from './SectionSoumission';
import ModalProfilMembre from './ModalProfilMembre';
import BottomActionBar from './BottomActionBar';
import {
  numeroCandidature,
  titreAnnulationCandidature,
  texteAnnulationCandidature,
  ligneAnnulation,
} from '../../annulationCandidatureTexte';
import type { InfosAnnulation } from '../../annulationCandidatureTexte';
import type { ProfileData } from '../../../../services/profileService';
import SectionPliable from '../../../../composants/SectionPliable/SectionPliable';
import './DetailCandidature.css';
import { getStatusColor } from '../../../../utils/entityUtils';
import { useLotImage } from '../../../../utils/getLotImage';
import SuiviHeader from '../../../../composants/SuiviHeader/SuiviHeader';
import TabAvisDocuments from './TabAvisDocuments';
import TabQuestionsReponses from './TabQuestionsReponses';
import DetailCandidatureSkeleton from './DetailCandidatureSkeleton';
import CGVAccept from '../../../../composants/CGVAccept/CGVAccept';
import Banner from '../../../../composants/Banner/Banner';

// ── Types ─────────────────────────────────────────────────────────────────────

interface EntrepriseInfo {
  id: number;
  nom: string;
  siret: string;
  representant?: string;
  email?: string;
  telephone?: string;
  progression: number;
}

interface GroupementMember {
  id: number;
  entreprise: EntrepriseInfo;
  entite_profil?: {
    id: number;
    nom: string;
    siret: string;
    progression: number;
  } | null;
  accepted_at: string | null;
  status: 'pending' | 'accepted' | 'refused';
  // Vrai si cette ligne est l'entreprise qui consulte (cf. CandidatureController::show).
  is_self?: boolean;
}

const PRIX_LIGNE_VIDE: PrixLigne = { prix_a: '', prix_b: '', prix_c: '' };

/**
 * Formate un prix API (ex: "12.0000") en affichage propre (ex: "12").
 * Garde les decimales significatives : "12.50" → "12.5", "12.0000" → "12".
 * Les valeurs nulles/vides restent vides.
 */
function formatPrixApi(val: number | string | null | undefined): string {
  if (val === null || val === undefined || val === '') return '';
  const num = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(num)) return '';
  // Supprime les zeros inutiles : 12.0000 → "12", 12.5000 → "12.5", 12.3400 → "12.34"
  return String(parseFloat(num.toFixed(4)));
}

interface LotDisponible {
  id: number;
  region: boolean;
  departement?: {
    id: number;
    nom: string;
    id_region?: number;
    region?: { id: number; nom: string };
  };
}

// Helpers pour la logique lots 1xx, 3xx (régionaux) / 2xx (départementaux)
const lotCodeIsRegional = (code: string): boolean => {
  const n = parseInt(code, 10);
  return (n >= 100 && n < 200) || (n >= 300 && n < 400);
};

/**
 * Construit les groupes de régions depuis les lots_departements d'une candidature.
 * Utilisé pour les lots 1xx : regroupe les lots_départements par région.
 */
const buildRegionGroups = (
  lotsDepartements: Array<{ id: number; departement?: { id: number; nom: string; region?: { id: number; nom: string } } }>,
  selectedIds: number[],
): RegionGroup[] => {
  const map = new Map<number, RegionGroup>();
  lotsDepartements
    .filter((l) => selectedIds.includes(l.id))
    .forEach((l) => {
      const regionId = l.departement?.region?.id ?? 0;
      const regionNom = l.departement?.region?.nom ?? 'Région inconnue';
      if (!map.has(regionId)) map.set(regionId, { regionId, regionNom, lots: [] });
      map.get(regionId)!.lots.push({ id: l.id, departement: l.departement });
    });
  return Array.from(map.values()).sort((a, b) => a.regionNom.localeCompare(b.regionNom));
};

interface CandidatureDetail {
  id: number;
  identifiant_candidature?: number | null;
  ref_publication?: string | null;
  status: string;
  deposee_at?: string | null;
  /** Calcule par le back : seul juge de la fenetre d'annulation. */
  peut_annuler?: boolean;
  motif_refus_annulation?: string | null;
  /** Faits de l'annulation lus dans l'historique. null hors annulation. */
  annulation?: InfosAnnulation | null;
  /**
   * Cloture du depot par la date limite de la phase de CETTE candidature.
   * Calcule par le back, jamais rejoue ici : le front ne compare aucune date.
   * Vrai aussi sur une candidature deja deposee, d'ou le filtrage sur le statut
   * brouillon partout ou l'information sert a alerter.
   */
  depot_clos?: boolean;
  /** Libelle du refus, identique a l'erreur que POST /deposer renverrait. */
  motif_refus_depot?: string | null;
  entite_id: number;
  is_mandataire: boolean;
  lots_disponibles?: LotDisponible[];
  lots_geo_ids: number[];
  accord_cadre_id: number;
  accord_cadre: {
    id: number;
    nom: string;
    acheteur?: string;
    ponderation_technique?: number | string;
    ponderation_financiere?: number | string;
    bareme_humain?: number | string;
    bareme_operationnel?: number | string;
    bareme_environnement?: number | string;
    reference_avis?: string;
    expire_at?: string;
    budget_estime?: number;
    source_financement?: string;
    type_marche_ccag?: string;
    categorie_cpv?: string;
    date_limite_candidature?: string;
    langue?: string;
    delai_validite_offre?: string;
    presentation_consultation?: string;
  };
  lot_technique: {
    id: number;
    code: string;
    nom: string;
    cpv?: string;
    articles?: Article[];
  };
  lots_departements: Array<{
    id: number;
    id_departement?: number;
    departement?: {
      id: number;
      nom: string;
      region?: { id: number; nom: string };
    };
  }>;
  groupement_members?: GroupementMember[];
  entite: {
    id: number;
    nom: string;
    siret: string;
    progression: number;
    representant?: string;
    email?: string;
    telephone?: string;
  };
  offre_technique?: {
    description_operationnel?: string | null;
    description_humain?: string | null;
    description_environnement?: string | null;
  };
  offre_financiere?: {
    prix_identiques: boolean;
    prix?: Array<{
      article_id: number;
      lot_departement_id: number;
      prix_a: number | null;
      prix_b: number | null;
      prix_c: number | null;
    }>;
  };
  convention_vierge?: { id: number; filename: string; mime_type?: string; filesize?: number } | null;
  convention_signee?: { id: number; filename: string; created_at?: string } | null;
  preview_disponible?: boolean;
}

interface CheckProfilsResult {
  all_complete: boolean;
  profils: Array<{
    entreprise_id: number;
    entreprise_nom: string;
    progression: number;
    mandataire: boolean;
  }>;
}

// ── Composant principal ───────────────────────────────────────────────────────

const DetailCandidature: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('token');
  const authHeaders: HeadersInit = { Authorization: `Bearer ${token}` };

  const [candidature, setCandidature] = useState<CandidatureDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(1); // "Candidature" par défaut
  const [showInviterModal, setShowInviterModal] = useState(false);

  const lotImage = useLotImage(candidature?.lot_technique?.nom);

  // Offre technique
  const [offreTechnique, setOffreTechnique] = useState<Record<string, string>>({});

  // Offre financière
  const [prixIdentiques, setPrixIdentiques] = useState(true);
  const [prixLignes, setPrixLignes] = useState<Record<number, PrixLigne>>({});
  const [prixParLot, setPrixParLot] = useState<Record<number, Record<number, PrixLigne>>>({});
  /** Prix par région pour lots 1xx en mode "prix différents par région" */
  const [prixParRegion, setPrixParRegion] = useState<Record<number, Record<number, PrixLigne>>>({});

  // Lots géo locaux (pour mise à jour sans rechargement API)
  const [lotsGeoIds, setLotsGeoIds] = useState<number[]>([]);

  // Popup confirmation toggle prix (Story 13.5)
  const [showTogglePrixConfirm, setShowTogglePrixConfirm] = useState(false);

  // Soumission (Story 8.9)
  const [checkProfils, setCheckProfils] = useState<CheckProfilsResult | null>(null);
  const [isDeposing, setIsDeposing] = useState(false);
  const [acceptConditions, setAcceptConditions] = useState(false);
  const [showCguModal, setShowCguModal] = useState(false);
  const [cguModalChecked, setCguModalChecked] = useState(false);
  const [showConfirmDeposer, setShowConfirmDeposer] = useState(false);
  // Annulation irreversible de la candidature (le back refuse des que le
  // resultat a ete notifie ou qu'un contrat est genere).
  const [showConfirmAnnuler, setShowConfirmAnnuler] = useState(false);
  const [isAnnulling, setIsAnnulling] = useState(false);
  const [annulerError, setAnnulerError] = useState<string | null>(null);
  const [deposerError, setDeposerError] = useState<string | null>(null);
  const [nomSignataire, setNomSignataire] = useState('');

  // EPIC-14 -- Preview PDF
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  // Story 8.12 — Modale profil membre groupement
  const [showMemberProfileModal, setShowMemberProfileModal] = useState(false);
  const [memberProfile, setMemberProfile] = useState<ProfileData | null>(null);
  const [memberProfileLoading, setMemberProfileLoading] = useState(false);
  const [memberProfileError, setMemberProfileError] = useState<string | null>(null);

  // Story 31.3 — Convention de groupement
  const [conventionSigneeUploading, setConventionSigneeUploading] = useState(false);
  const [conventionPreviewBlobUrl, setConventionPreviewBlobUrl] = useState<string | null>(null);
  const [conventionPreviewFilename, setConventionPreviewFilename] = useState<string>('convention-signee.pdf');

  // Retrait membre groupement
  const [showRetirerModal, setShowRetirerModal] = useState(false);
  const [retirerTarget, setRetirerTarget] = useState<{ id: number; nom: string } | null>(null);
  const [retirerLoading, setRetirerLoading] = useState(false);

  // Story 8.5 — Invitation membre groupement
  const [inviteSearchQuery, setInviteSearchQuery] = useState('');
  const [inviteSearchResults, setInviteSearchResults] = useState<Array<{ id: number; nom: string; type: string; siret?: string; entreprise_id: number | null }>>([]);
  const [inviteIsSearching, setInviteIsSearching] = useState(false);
  const [inviteMembres, setInviteMembres] = useState<Array<{ id: number; nom: string; type: string; siret?: string; entreprise_id: number | null }>>([]);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const inviteSearchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Garde contre les reponses de chargement perimees. Une reponse dont la requete est partie
  // avant la derniere saisie, ou pendant qu'une sauvegarde etait en attente, ne doit pas
  // reecrire les etats editables : la base ne reflete pas encore la saisie locale. Constate en
  // prod le 2026-09-07 sur une grille de 8 160 lignes (GET de 7 a 9 s) : la prod est servie par
  // le serveur de developpement, donc StrictMode y montait l'effet deux fois, la seconde reponse
  // arrivait apres la saisie, la remplacait, et l'auto-save renvoyait la valeur perimee.
  const saisieVersionRef = useRef(0);
  const saisiesNonPersisteesRef = useRef(false);
  const marquerSaisie = () => { saisieVersionRef.current += 1; };

  const TABS_DETAIL = ['Avis et documents', 'Candidature', 'Questions/Réponses (Support technique)'];

  // Seul enrichissement nécessaire : extraire lots_geo_ids depuis lots_departements
  const normalizeApiData = (raw: any): CandidatureDetail => ({
    ...raw,
    lots_geo_ids: (raw.lots_departements ?? []).map((l: any) => l.id),
  });

  /** Charge (ou recharge) check-profils depuis l'API */
  const loadCheckProfils = useCallback(() => {
    if (!id) return;
    fetch(`${apiUrl}/publications/ac27/candidatures/${id}/check-profils`, { headers: authHeaders })
      .then((r) => r.json())
      .then((d) => setCheckProfils(d?.data ?? d))
      .catch(() => { /* silencieux — les items resteront à null */ });
  }, [id, apiUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Story 8.12 — Ouvre la modale profil membre et charge les données */
  const openMemberProfileModal = useCallback((entrepriseId: number) => {
    setMemberProfile(null);
    setMemberProfileError(null);
    setMemberProfileLoading(true);
    setShowMemberProfileModal(true);

    fetch(
      `${apiUrl}/publications/ac27/candidatures/${id}/membres/${entrepriseId}/profil`,
      { headers: authHeaders },
    )
      .then((r) => {
        if (r.status === 403) throw new Error('Accès refusé : vous n\'êtes pas mandataire de cette candidature.');
        if (r.status === 404) throw new Error('Ce membre n\'est pas un cotraitant accepté de cette candidature.');
        if (!r.ok) throw new Error(`Erreur ${r.status}`);
        return r.json();
      })
      .then((d) => setMemberProfile(d?.data ?? d))
      .catch((err: Error) => setMemberProfileError(err.message || 'Impossible de charger le profil.'))
      .finally(() => setMemberProfileLoading(false));
  }, [id, apiUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Story 31.3 — Télécharger la convention vierge (modèle fourni par l'admin)
  const handleDownloadConventionVierge = useCallback(async () => {
    if (!candidature?.convention_vierge || !candidature?.accord_cadre_id) return;
    const res = await fetch(
      `${apiUrl}/publications/ac27/accords-cadres/${candidature.accord_cadre_id}/documents/${candidature.convention_vierge.id}/download`,
      { headers: authHeaders },
    );
    if (!res.ok) return;
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = candidature.convention_vierge.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }, [candidature, apiUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Story 31.3 — Prévisualiser la convention signée (ouvre popup avec iframe)
  const handleDownloadConventionSignee = useCallback(async () => {
    if (!id) return;
    const res = await fetch(
      `${apiUrl}/publications/ac27/candidatures/${id}/convention-groupement-signee`,
      { headers: authHeaders },
    );
    if (!res.ok) return;
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const filename = candidature?.convention_signee?.filename ?? 'convention-signee.pdf';
    setConventionPreviewFilename(filename);
    setConventionPreviewBlobUrl(url);
  }, [id, apiUrl, candidature]); // eslint-disable-line react-hooks/exhaustive-deps

  // Story 31.3 — Télécharger depuis la popup de preview
  const handleDownloadConventionFromPreview = useCallback(() => {
    if (!conventionPreviewBlobUrl) return;
    const a = document.createElement('a');
    a.href = conventionPreviewBlobUrl;
    a.download = conventionPreviewFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [conventionPreviewBlobUrl, conventionPreviewFilename]);

  // Fermer la preview et libérer la mémoire
  const handleCloseConventionPreview = useCallback(() => {
    if (conventionPreviewBlobUrl) window.URL.revokeObjectURL(conventionPreviewBlobUrl);
    setConventionPreviewBlobUrl(null);
  }, [conventionPreviewBlobUrl]);

  // Story 31.3 — Uploader la convention signée par le mandataire
  const handleUploadConventionSignee = useCallback(async (file: File) => {
    if (!id) return;
    setConventionSigneeUploading(true);
    const formData = new FormData();
    formData.append('fichier', file);
    try {
      const res = await fetch(`${apiUrl}/publications/ac27/candidatures/${id}/convention-groupement-signee`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      loadCandidature();
    } catch {
      // silencieux
    } finally {
      setConventionSigneeUploading(false);
    }
  }, [id, apiUrl, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Story 31.3 — Supprimer la convention signée
  const handleDeleteConventionSignee = useCallback(async () => {
    if (!id) return;
    await fetch(`${apiUrl}/publications/ac27/candidatures/${id}/convention-groupement-signee`, {
      method: 'DELETE',
      headers: authHeaders,
    });
    loadCandidature();
  }, [id, apiUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCandidature = useCallback((signal?: AbortSignal) => {
    if (!id) return Promise.resolve();
    const versionAuDepart = saisieVersionRef.current;
    const saisiesNonPersisteesAuDepart = saisiesNonPersisteesRef.current;
    return fetch(`${apiUrl}/publications/ac27/candidatures/${id}`, { headers: authHeaders, signal })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => {
        if (signal?.aborted) return;
        const raw = d?.data ?? d;
        const data = normalizeApiData(raw);
        setCandidature(data);
        // Les etats editables ne sont pris de la reponse que si elle reflete la saisie locale
        if (saisiesNonPersisteesAuDepart || saisieVersionRef.current !== versionAuDepart) return;
        setLotsGeoIds(data.lots_geo_ids ?? []);
        setNomSignataire(data.representant_signature_nom || data.entite?.representant || '');
        const identiques = data.offre_financiere?.prix_identiques ?? true;
        setPrixIdentiques(identiques);

        const ot = data.offre_technique ?? {};
        setOffreTechnique({
          description_operationnel: ot.description_operationnel ?? '',
          description_humain: ot.description_humain ?? '',
          description_environnement: ot.description_environnement ?? '',
        });

        // Parser les prix depuis l'API (tableau article × département)
        const prixApi = data.offre_financiere?.prix ?? [];
        const isReg = lotCodeIsRegional(raw.lot_technique?.code ?? '0');

        if (identiques) {
          // Tous les départements ont les mêmes prix → prendre le premier lot rencontré
          const lignes: Record<number, PrixLigne> = {};
          prixApi.forEach((p) => {
            if (!lignes[p.article_id]) {
              lignes[p.article_id] = {
                prix_a: formatPrixApi(p.prix_a),
                prix_b: formatPrixApi(p.prix_b),
                prix_c: formatPrixApi(p.prix_c),
              };
            }
          });
          setPrixLignes(lignes);
        } else if (isReg) {
          // Lot 1xx prix différents → grouper par RÉGION (prendre le premier dept de chaque région)
          const lotsDepts = data.lots_departements ?? [];
          // Map lot_departement_id → region_id
          const lotToRegion: Record<number, number> = {};
          lotsDepts.forEach((l: any) => {
            const regionId = l.departement?.region?.id ?? 0;
            lotToRegion[l.id] = regionId;
          });
          const byRegion: Record<number, Record<number, PrixLigne>> = {};
          prixApi.forEach((p) => {
            const regionId = lotToRegion[p.lot_departement_id] ?? 0;
            if (!byRegion[regionId]) byRegion[regionId] = {};
            // Prendre le premier prix rencontré pour chaque article de la région
            if (!byRegion[regionId][p.article_id]) {
              byRegion[regionId][p.article_id] = {
                prix_a: formatPrixApi(p.prix_a),
                prix_b: formatPrixApi(p.prix_b),
                prix_c: formatPrixApi(p.prix_c),
              };
            }
          });
          setPrixParRegion(byRegion);
        } else {
          // Lot 2xx prix différents → grouper par lot_departement_id
          const byLot: Record<number, Record<number, PrixLigne>> = {};
          prixApi.forEach((p) => {
            if (!byLot[p.lot_departement_id]) byLot[p.lot_departement_id] = {};
            byLot[p.lot_departement_id][p.article_id] = {
              prix_a: formatPrixApi(p.prix_a),
              prix_b: formatPrixApi(p.prix_b),
              prix_c: formatPrixApi(p.prix_c),
            };
          });
          setPrixParLot(byLot);
        }
      })
      .then(() => { if (!signal?.aborted) loadCheckProfils(); })
      .catch((e) => {
        if (e?.name === 'AbortError') return;
        // Un echec de rechargement ne doit pas faire disparaitre une grille en cours de saisie :
        // l'etat affiche est conserve, seul le chargement initial reste sans candidature
        console.error('Chargement de la candidature en echec', e);
      })
      .finally(() => { if (!signal?.aborted) setLoading(false); });
  }, [id, apiUrl, loadCheckProfils]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // StrictMode (actif en prod, servie par le serveur de developpement) demonte puis remonte
    // l'effet de facon synchrone au montage : le chargement est differe d'un tick pour que celui
    // du premier montage soit annule avant d'etre envoye, donc un seul GET au lieu de deux et
    // aucune requete inutile a traiter cote serveur. Le signal couvre le vrai demontage de la
    // page pendant la requete.
    const controleur = new AbortController();
    const tick = setTimeout(() => { loadCandidature(controleur.signal); }, 0);
    return () => { clearTimeout(tick); controleur.abort(); };
  }, [loadCandidature]);

  // ── buildOffreFinancierePayload (déplacé avant les hooks auto-save) ────────

  const buildOffreFinancierePayload = useCallback(() => {
    const prix: Array<{
      article_id: number;
      lot_departement_id: number;
      prix_a: number | null;
      prix_b: number | null;
      prix_c: number | null;
    }> = [];

    const parsePrix = (val: string): number | null => {
      if (val === '' || val === null || val === undefined) return null;
      const n = parseFloat(val);
      return isNaN(n) ? null : n;
    };

    const isReg = lotCodeIsRegional(candidature?.lot_technique?.code ?? '0');
    const selectedLotsDepts = (candidature?.lots_departements ?? []).filter((l) =>
      lotsGeoIds.includes(l.id)
    );

    if (prixIdentiques) {
      lotsGeoIds.forEach((lotId) => {
        Object.entries(prixLignes).forEach(([articleId, p]) => {
          prix.push({
            article_id: Number(articleId),
            lot_departement_id: lotId,
            prix_a: parsePrix(p.prix_a),
            prix_b: parsePrix(p.prix_b),
            prix_c: parsePrix(p.prix_c),
          });
        });
      });
    } else if (isReg) {
      selectedLotsDepts.forEach((l) => {
        const regionId = l.departement?.region?.id ?? 0;
        const regionPrix = prixParRegion[regionId] ?? {};
        Object.entries(regionPrix).forEach(([articleId, p]) => {
          prix.push({
            article_id: Number(articleId),
            lot_departement_id: l.id,
            prix_a: parsePrix(p.prix_a),
            prix_b: parsePrix(p.prix_b),
            prix_c: parsePrix(p.prix_c),
          });
        });
      });
    } else {
      lotsGeoIds.forEach((lotId) => {
        const articlesLot = prixParLot[lotId] ?? {};
        Object.entries(articlesLot).forEach(([articleId, p]) => {
          prix.push({
            article_id: Number(articleId),
            lot_departement_id: lotId,
            prix_a: parsePrix(p.prix_a),
            prix_b: parsePrix(p.prix_b),
            prix_c: parsePrix(p.prix_c),
          });
        });
      });
    }

    return { prix_identiques: prixIdentiques, prix };
  }, [candidature, lotsGeoIds, prixIdentiques, prixLignes, prixParLot, prixParRegion]);

  // ── Auto-Save hooks (Story 13.1/13.2/13.3/13.4) ──────────────────────────

  // Auto-save lots (Story 13.2)
  const lotsAutoSave = useAutoSave({
    data: lotsGeoIds,
    saveFn: async (ids) => {
      const res = await fetch(`${apiUrl}/publications/ac27/candidatures/${id}/lots-departements`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ lot_departement_ids: ids, prix_identiques: prixIdentiques }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const error: any = new Error(err?.message ?? `Erreur ${res.status}`);
        error.status = res.status;
        error.errors = err?.errors;
        throw error;
      }
      // Re-fetch complet pour récupérer les prix auto-créés par le backend
      await loadCandidature();
    },
    delay: 2000,
    enabled: candidature?.status === 'brouillon' && candidature?.is_mandataire === true,
  });

  // Auto-save offre technique (Story 13.3)
  const otAutoSave = useAutoSave({
    data: offreTechnique,
    saveFn: async (data) => {
      const res = await fetch(`${apiUrl}/publications/ac27/candidatures/${id}/offre-technique`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const error: any = new Error(err?.message ?? `Erreur ${res.status}`);
        error.status = res.status;
        error.errors = err?.errors;
        throw error;
      }
    },
    delay: 2000,
    enabled: candidature?.status === 'brouillon' && candidature?.is_mandataire === true,
  });

  // Auto-save offre financière (Story 13.4)
  const isRegionalForAutoSave = lotCodeIsRegional(candidature?.lot_technique?.code ?? '0');

  const offreFinanciereData = useMemo(() => ({
    prixIdentiques,
    prixLignes: prixIdentiques ? prixLignes : undefined,
    prixParLot: !prixIdentiques && !isRegionalForAutoSave ? prixParLot : undefined,
    prixParRegion: !prixIdentiques && isRegionalForAutoSave ? prixParRegion : undefined,
  }), [prixIdentiques, prixLignes, prixParLot, prixParRegion, isRegionalForAutoSave]);

  // Auto-save nom du signataire (EPIC-50)
  const nomSignataireAutoSave = useAutoSave({
    data: nomSignataire,
    saveFn: async (nom) => {
      const res = await fetch(`${apiUrl}/publications/ac27/candidatures/${id}/signataire-nom`, {
        method: 'PATCH',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ representant_signature_nom: nom.trim() || null }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.message ?? `Erreur ${res.status}`);
      }
    },
    delay: 1500,
    enabled: candidature?.status === 'brouillon' && candidature?.is_mandataire === true,
  });

  const ofAutoSave = useAutoSave({
    data: offreFinanciereData,
    saveFn: async () => {
      if (!candidature) return;
      const payload = buildOffreFinancierePayload();
      const res = await fetch(`${apiUrl}/publications/ac27/candidatures/${id}/offre-financiere`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const error: any = new Error(err?.message ?? `Erreur ${res.status}`);
        error.status = res.status;
        error.errors = err?.errors;
        throw error;
      }
    },
    delay: 2000,
    enabled: candidature?.status === 'brouillon' && candidature?.is_mandataire === true,
  });

  // ── Status global de sauvegarde (centralise dans la bottom bar) ──────────

  const globalSaveStatus: AutoSaveStatus = useMemo(() => {
    const statuses = [lotsAutoSave.status, otAutoSave.status, ofAutoSave.status, nomSignataireAutoSave.status];
    if (statuses.some((s) => s === 'error')) return 'error';
    if (statuses.some((s) => s === 'saving')) return 'saving';
    if (statuses.some((s) => s === 'pending')) return 'pending';
    if (statuses.some((s) => s === 'saved')) return 'saved';
    return 'idle';
  }, [lotsAutoSave.status, otAutoSave.status, ofAutoSave.status, nomSignataireAutoSave.status]);

  const globalSaveError = lotsAutoSave.error ?? otAutoSave.error ?? ofAutoSave.error ?? nomSignataireAutoSave.error ?? null;

  // Miroir lisible depuis loadCandidature sans dependre du cycle de rendu. Calcule sur les quatre
  // statuts bruts et non sur globalSaveStatus, qui fait passer une erreur devant une attente ;
  // une sauvegarde en erreur compte aussi comme non persistee.
  useEffect(() => {
    saisiesNonPersisteesRef.current = [lotsAutoSave.status, otAutoSave.status, ofAutoSave.status, nomSignataireAutoSave.status]
      .some((s) => s === 'pending' || s === 'saving' || s === 'error');
  }, [lotsAutoSave.status, otAutoSave.status, ofAutoSave.status, nomSignataireAutoSave.status]);

  // ── Protection navigation (Story 13.7) ──────────────────────────────────────

  const hasPendingChanges = lotsAutoSave.status === 'pending' || otAutoSave.status === 'pending' || ofAutoSave.status === 'pending' || nomSignataireAutoSave.status === 'pending';

  // ── Story 41.5 — Réinitialiser CG + PDF lorsque les données changent ─────
  const dataFingerprint = useMemo(() =>
    JSON.stringify({ offreTechnique, prixLignes, prixParLot, prixParRegion, lotsGeoIds, nomSignataire }),
    [offreTechnique, prixLignes, prixParLot, prixParRegion, lotsGeoIds, nomSignataire]
  );
  const dataFingerprintRef = useRef(dataFingerprint);

  useEffect(() => {
    // Ignorer le premier rendu (chargement initial)
    if (dataFingerprintRef.current === dataFingerprint) return;
    dataFingerprintRef.current = dataFingerprint;

    // Ne pas réinitialiser si la candidature est déjà déposée
    if (candidature?.status !== 'brouillon') return;

    // Réinitialiser CG et PDF si les données changent
    if (acceptConditions) {
      setAcceptConditions(false);
    }
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  }, [dataFingerprint]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Story 41.5 — Auto-générer le PDF quand les CG sont acceptées ──────────
  useEffect(() => {
    if (acceptConditions && !pdfUrl && !isGeneratingPdf) {
      handlePreviewPdf();
    }
  }, [acceptConditions]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Story 41.5 — Restaurer CG + délibération + PDF au chargement si preview dispo serveur
  useEffect(() => {
    if (candidature?.preview_disponible && !pdfUrl && !isGeneratingPdf && !acceptConditions) {
      setAcceptConditions(true);
    }
  }, [candidature?.preview_disponible]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Générer le PDF pour les candidatures déjà déposées ───────────────────
  useEffect(() => {
    if (candidature?.status !== 'brouillon' && !pdfUrl && !isGeneratingPdf) {
      handlePreviewPdf();
    }
  }, [candidature?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasPendingChanges) {
        e.preventDefault();
        lotsAutoSave.flush();
        otAutoSave.flush();
        ofAutoSave.flush();
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden' && hasPendingChanges) {
        lotsAutoSave.flush();
        otAutoSave.flush();
        ofAutoSave.flush();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [hasPendingChanges]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handler "Quitter" (Story 13.7) ──────────────────────────────────────────

  const handleQuitter = async () => {
    await lotsAutoSave.flush();
    await Promise.all([otAutoSave.flush(), ofAutoSave.flush()]);
    navigate('/publications?tab=4');
  };

  // ── Handler "Preview PDF" (EPIC-14) ─────────────────────────────────────────

  const handlePreviewPdf = async () => {
    setIsGeneratingPdf(true);
    setPdfError(null);

    // Scroller vers la zone PDF pour montrer le spinner
    setTimeout(() => {
      document.getElementById('pdf-preview-zone')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);

    // 1. Flush toutes les sauvegardes en attente (EPIC-13)
    try {
      await lotsAutoSave.flush();
      await Promise.all([otAutoSave.flush(), ofAutoSave.flush()]);
    } catch (e) {
      setIsGeneratingPdf(false);
      setPdfError('Veuillez corriger les erreurs avant de previsualiser le PDF.');
      return;
    }

    // 2. Appeler l'endpoint preview-pdf
    try {
      const res = await fetch(
        `${apiUrl}/publications/ac27/candidatures/${id}/preview-pdf`,
        { headers: authHeaders }
      );

      if (res.status === 422) {
        const err = await res.json();
        setPdfError(
          'Candidature incomplete : ' +
          (err.errors ?? []).join(', ')
        );
        return;
      }

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.message || `Erreur ${res.status}`);
      }

      // 3. Stocker le blob URL pour la modale PDF
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);

      // Scroller vers la zone PDF pour que l'utilisateur la voie
      setTimeout(() => {
        document.getElementById('pdf-preview-zone')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } catch (e: any) {
      setPdfError(e.message || 'Erreur lors de la generation du PDF.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const getDisabledTabs = useCallback(() => {
    if (!candidature) return [];

    const disabledTabs: number[] = [];

    return disabledTabs;
  }, [candidature]);

  const getInactiveTabs = useCallback(() => {
    if (!candidature) return [];
    const inactiveTabs: number[] = [];

    return inactiveTabs;
  }, [candidature]);

  const handleTabChange = useCallback((tabIndex: number) => {
    const disabledTabs = getDisabledTabs();
    const inactiveTabs = getInactiveTabs();
    if (!disabledTabs.includes(tabIndex) || !inactiveTabs.includes(tabIndex)) {
      setActiveTab(tabIndex);
    }
  }, [getDisabledTabs, getInactiveTabs]);

  if (loading) {
    return <DetailCandidatureSkeleton />;
  }

  if (!candidature) {
    return (
      <div className="detail-error">
        <p>Candidature introuvable.</p>
        <Button
          type="button"
          variant="secondary"
          value="Retour"
          size="small"
          onClick={() => navigate('/publications?tab=0')}
        />
      </div>
    );
  }

  const estMandataire = candidature.is_mandataire === true;
  const isRegional = lotCodeIsRegional(candidature.lot_technique.code);

  // Brouillon dont l'echeance de depot est passee : il restera consultable mais
  // ne pourra plus etre depose. Le statut est indispensable dans la condition —
  // depot_clos vaut aussi vrai sur une candidature DEJA deposee, pour qui
  // l'alerte n'aurait aucun sens. Un seul calcul, consomme par le bandeau de
  // tete, celui de la section 5 et le grisage du bouton : les trois ne peuvent
  // pas diverger.
  const depotHorsDelai = candidature.status === 'brouillon' && candidature.depot_clos === true;

  // Groupes de régions pour lots 1xx (lots_departements actuellement sélectionnés)
  const regionGroups: RegionGroup[] = isRegional
    ? buildRegionGroups(candidature.lots_departements, lotsGeoIds)
    : [];

  // ── Validation pré-soumission ────────────────────────────────────────────────

  const lotsOk = lotsGeoIds.length > 0;

  const offreTechniqueOk = CRITERES_TECHNIQUES.every(
    (c) => (offreTechnique[c.slug] ?? '').trim().length > 0,
  );

  const offreFinanciereOk = (() => {
    const articles = candidature.lot_technique.articles ?? [];
    if (articles.length === 0) return true;
    if (prixIdentiques) {
      return articles.every((a) => {
        const p = prixLignes[a.id];
        return p && p.prix_a !== '' && p.prix_b !== '' && p.prix_c !== '';
      });
    }
    if (isRegional) {
      // Lot 1xx : vérifier que chaque région a tous ses articles renseignés
      return regionGroups.length > 0 && regionGroups.every(({ regionId }) => {
        const regPrix = prixParRegion[regionId];
        return articles.every((a) => {
          const p = regPrix?.[a.id];
          return p && p.prix_a !== '' && p.prix_b !== '' && p.prix_c !== '';
        });
      });
    }
    return lotsGeoIds.length > 0 && lotsGeoIds.every((lotId) => {
      const lotPrix = prixParLot[lotId];
      return articles.every((a) => {
        const p = lotPrix?.[a.id];
        return p && p.prix_a !== '' && p.prix_b !== '' && p.prix_c !== '';
      });
    });
  })();

  const validationItems: ValidationItem[] = [
    {
      label: 'Lots géographiques',
      ok: lotsOk,
      detail: lotsOk ? `${lotsGeoIds.length} lot(s) sélectionné(s)` : 'Sélectionnez au moins un lot géographique',
    },
    {
      label: 'Offre technique',
      ok: offreTechniqueOk,
      detail: offreTechniqueOk ? 'Les 4 critères sont renseignés' : 'Renseignez les 4 critères techniques',
    },
    {
      label: 'Offre financière',
      ok: offreFinanciereOk,
      detail: offreFinanciereOk ? 'Tous les prix sont renseignés' : 'Renseignez tous les prix',
    },
    ...(checkProfils
      ? checkProfils.profils.map((p: any) => ({
        label: `${p.entreprise_nom} (${p.mandataire ? 'mandataire' : 'cotraitant'})`,
        ok: p.invitation_pending ? false : p.progression === 100,
        detail: p.invitation_pending
          ? 'Invitation en attente — doit accepter avant le dépôt'
          : p.progression === 100 ? 'Profil complet' : `Profil à ${p.progression}% — doit être à 100% avant dépôt`,
      }))
      : []
    ),
    ...((candidature.groupement_members?.length ?? 0) > 0
      ? [{
        label: 'Convention de groupement signée',
        ok: !!candidature.convention_signee,
        detail: candidature.convention_signee
          ? `Convention déposée : ${candidature.convention_signee.filename}`
          : 'Déposez la convention de groupement signée (obligatoire pour les groupements)',
      }]
      : []
    ),
    {
      label: 'Nom du signataire',
      ok: nomSignataire.trim().length > 0,
      detail: nomSignataire.trim().length > 0 ? `Signataire : ${nomSignataire.trim()}` : 'Renseignez le nom du signataire (section 5)',
    },
  ];

  // ── Handlers ────────────────────────────────────────────────────────────────

  /**
   * Affiche le contenu de l'onglet actif
   */
  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <TabAvisDocuments accordCadre={candidature.accord_cadre} />
        );
      case 1:
        return (
          <>
            {/* Section 1 — Candidat / Groupement */}
            <SectionPliable title="1- Candidature" className="candidature-section-pliable">
            <SectionCandidat
              entite={candidature.entite}
              groupementMembers={candidature.groupement_members ?? []}
              estMandataire={estMandataire}
              isSoumise={candidature.status !== 'brouillon'}
              onRetirer={handleRetirerMembre}
              onInviter={() => setShowInviterModal(true)}
              onViewMemberProfile={openMemberProfileModal}
              conventionVierge={candidature.convention_vierge}
              conventionSignee={candidature.convention_signee}
              onDownloadConventionVierge={handleDownloadConventionVierge}
              onDownloadConventionSignee={handleDownloadConventionSignee}
              onUploadConventionSignee={handleUploadConventionSignee}
              onDeleteConventionSignee={handleDeleteConventionSignee}
              conventionSigneeUploading={conventionSigneeUploading}
            />
            {/* Solo (aucun cotraitant invité) : point d'entrée pour constituer un groupement.
                Une candidature à 0 membre n'est accessible que par son propriétaire (cf. back
                assertPeutAcceder), donc pas besoin de re-tester estMandataire ici — ce qui rend
                le bouton robuste même si le flag pivot mandataire n'est pas posé (données seed/ETL).
                Dès qu'un membre existe, le bouton d'ajout vit dans le footer de SectionCandidat. */}
            {(candidature.groupement_members?.length ?? 0) === 0 && candidature.status === 'brouillon' && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <Button
                  type="button"
                  variant="primary"
                  value="Ajouter un membre au groupement"
                  size="small"
                  onClick={() => setShowInviterModal(true)}
                />
              </div>
            )}
            </SectionPliable>

            {/* Section 2 — Choix des lots */}
            <SectionPliable title="2- Choix des lots" className="candidature-section-pliable">
            <SectionChoixLots
              lotTechnique={candidature.lot_technique}
              lotsDisponibles={candidature.lots_disponibles ?? []}
              selectedIds={lotsGeoIds}
              canEdit={candidature.status === 'brouillon' && estMandataire}
              onLotsGeoChange={handleLotsGeoChange}
            />
            </SectionPliable>

            {/* Section 3 — Offre technique (pondération + barèmes lus depuis l'AC configuré ; fallback métier 55% et 4/5/1) */}
            <SectionPliable title={`3- Offre technique (pondérée à ${Math.round(Number(candidature.accord_cadre.ponderation_technique ?? 55))}%)`} className="candidature-section-pliable">
            <Banner
              variant="info"
              message="Par défaut les réponses sont remplies avec les éléments de votre profil, mais il vous appartient de les mettre à jour concernant cette consultation."
            />
            <SectionOffreTechnique
              poidsOffreTechnique={Math.round(Number(candidature.accord_cadre.ponderation_technique ?? 55))}
              baremes={{
                humain: candidature.accord_cadre.bareme_humain != null ? Number(candidature.accord_cadre.bareme_humain) : undefined,
                operationnel: candidature.accord_cadre.bareme_operationnel != null ? Number(candidature.accord_cadre.bareme_operationnel) : undefined,
                environnement: candidature.accord_cadre.bareme_environnement != null ? Number(candidature.accord_cadre.bareme_environnement) : undefined,
              }}
              canEdit={candidature.status === 'brouillon' && estMandataire}
              offreTechnique={offreTechnique}
              onChangeCritere={handleChangeCritere}
            />
            </SectionPliable>

            {/* Section 4 — Offre financière */}
            <SectionPliable title={`4- Offre financière (pondérée à ${Math.round(Number(candidature.accord_cadre.ponderation_financiere ?? 45))}%)`} className="candidature-section-pliable">
            <SectionOffreFinanciere
              poidsOffreFinanciere={Math.round(Number(candidature.accord_cadre.ponderation_financiere ?? 45))}
              articles={candidature.lot_technique.articles ?? []}
              canEdit={candidature.status === 'brouillon' && estMandataire}
              isRegional={isRegional}
              regionGroups={regionGroups}
              prixIdentiques={prixIdentiques}
              prixLignes={prixLignes}
              prixParLot={prixParLot}
              prixParRegion={prixParRegion}
              selectedLots={candidature.lots_departements.filter((l) => lotsGeoIds.includes(l.id))}
              apiErrors={ofAutoSave.validationErrors}
              onTogglePrixIdentiques={() => {
                if (prixIdentiques) {
                  // identiques → différents : PAS de popup, pré-remplir les accordéons
                  marquerSaisie();
                  setPrixIdentiques(false);
                  if (isRegional) {
                    const newPrixParRegion: Record<number, Record<number, PrixLigne>> = {};
                    regionGroups.forEach(({ regionId }) => {
                      newPrixParRegion[regionId] = { ...prixLignes };
                    });
                    setPrixParRegion(newPrixParRegion);
                  } else {
                    const newPrixParLot: Record<number, Record<number, PrixLigne>> = {};
                    lotsGeoIds.forEach((lotId) => {
                      newPrixParLot[lotId] = { ...prixLignes };
                    });
                    setPrixParLot(newPrixParLot);
                  }
                } else {
                  // différents → identiques : POPUP de confirmation
                  setShowTogglePrixConfirm(true);
                }
              }}
              onChangePrix={handleChangePrix}
              onChangePrixLot={handleChangePrixLot}
              onChangePrixRegion={handleChangePrixRegion}
            />
            </SectionPliable>

            {/* Section 5 — Révision + Soumission (Story 8.9) */}
            <SectionPliable title="5- Dépôt de la candidature et de l'offre" className="candidature-section-pliable">
            <SectionSoumission
              isSoumise={candidature.status !== 'brouillon'}
              isAnnulee={candidature.status === 'annulee'}
              infosAnnulation={candidature.annulation}
              depotHorsDelai={depotHorsDelai}
              motifRefusDepot={candidature.motif_refus_depot ?? null}
              deposee_at={candidature.deposee_at ?? null}
              estMandataire={estMandataire}
              validationItems={validationItems}
              isDeposing={isDeposing}
              acceptConditions={acceptConditions}
              onToggleAccept={() => setAcceptConditions((v) => !v)}
              onOpenCguModal={() => { setCguModalChecked(false); setShowCguModal(true); }}
              onDeposer={handleDeposer}
              onPreviewPdf={handlePreviewPdf}
              isGeneratingPdf={isGeneratingPdf}
              pdfUrl={pdfUrl}
              nomSignataire={nomSignataire}
              onNomSignataireChange={handleChangeNomSignataire}
            />
            </SectionPliable>
          </>
        );
      case 2:
        return (
          <TabQuestionsReponses accordCadreId={candidature.accord_cadre.id} />
        );
      default:
        return null;
    }
  };

  const handleLotsGeoChange = (ids: number[]) => {
    marquerSaisie();
    setLotsGeoIds(ids);
    setCandidature((prev) => prev ? { ...prev, lots_geo_ids: ids } : prev);
  };

  const handleRetirerMembre = (membreId: number, membreNom: string) => {
    setRetirerTarget({ id: membreId, nom: membreNom });
    setShowRetirerModal(true);
  };

  const confirmRetirerMembre = async () => {
    if (!retirerTarget || !id) return;
    setRetirerLoading(true);
    try {
      await fetch(
        `${apiUrl}/publications/ac27/candidatures/${id}/membres/${retirerTarget.id}`,
        { method: 'DELETE', headers: authHeaders },
      );
      setShowRetirerModal(false);
      setRetirerTarget(null);
      await loadCandidature();
    } catch {
      // silencieux — la popup reste ouverte
    } finally {
      setRetirerLoading(false);
    }
  };

  const handleChangeCritere = (slug: string, value: string) => {
    marquerSaisie();
    setOffreTechnique((prev) => ({ ...prev, [slug]: value }));
  };

  const handleChangeNomSignataire = (nom: string) => {
    marquerSaisie();
    setNomSignataire(nom);
  };

  // ── Story 8.5 — Invitation recherche autocomplete ─────────────────────────

  const handleInviteSearchChange = (q: string) => {
    setInviteSearchQuery(q);
    setInviteSearchResults([]);
    if (inviteSearchTimeout.current) clearTimeout(inviteSearchTimeout.current);
    if (q.trim().length < 2) { setInviteIsSearching(false); return; }
    setInviteIsSearching(true);
    inviteSearchTimeout.current = setTimeout(() => {
      fetch(
        `${apiUrl}/entites/autocomplete/${encodeURIComponent(q.trim())}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
        .then((r) => r.json())
        .then((d) => {
          const results: Array<{ id: number; nom: string; type: string; siret?: string; entreprise_id: number | null }> = Array.isArray(d?.data) ? d.data : [];
          const existingEntiteIds = new Set([
            candidature?.entite_id,
            ...(candidature?.groupement_members ?? []).map((m) => m.entreprise.id),
            ...inviteMembres.map((m) => m.id),
          ].filter(Boolean));
          setInviteSearchResults(results.filter((r) => !existingEntiteIds.has(r.id)));
        })
        .catch(() => setInviteSearchResults([]))
        .finally(() => setInviteIsSearching(false));
    }, 300);
  };

  const handleInviteAddMembre = (entite: { id: number; nom: string; type: string; siret?: string; entreprise_id: number | null }) => {
    if (!inviteMembres.some((m) => m.id === entite.id)) {
      setInviteMembres((prev) => [...prev, entite]);
    }
    setInviteSearchQuery('');
    setInviteSearchResults([]);
  };

  const handleInviteRemoveMembre = (entiteId: number) => {
    setInviteMembres((prev) => prev.filter((m) => m.id !== entiteId));
  };

  const handleInviteSubmit = async () => {
    if (inviteMembres.length === 0 || !id) return;
    setInviteSubmitting(true);
    setInviteError(null);
    try {
      const entiteIds = inviteMembres.map((m) => m.id);
      const res = await fetch(
        `${apiUrl}/publications/ac27/candidatures/${id}/inviter-membre`,
        {
          method: 'POST',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({ entite_ids: entiteIds }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const msg = err?.errors
          ? Object.values(err.errors as Record<string, string[]>).flat().join(' ')
          : err?.message ?? 'Une erreur est survenue.';
        setInviteError(msg);
        return;
      }
      setShowInviterModal(false);
      setInviteMembres([]);
      setInviteSearchQuery('');
      setInviteSearchResults([]);
      setInviteError(null);
      window.location.reload();
    } catch {
      setInviteError('Erreur réseau. Veuillez réessayer.');
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleCloseInviterModal = () => {
    setShowInviterModal(false);
    setInviteMembres([]);
    setInviteSearchQuery('');
    setInviteSearchResults([]);
    setInviteError(null);
  };

  // Limite à 4 décimales max, sans trailing zeros
  const sanitizePrix = (val: string): string => {
    if (val === '' || val === '-') return val;
    const match = val.match(/^(\d*\.?\d{0,4})/);
    return match ? match[1] : val;
  };

  const handleChangePrix = (
    articleId: number,
    champ: keyof PrixLigne,
    val: string,
  ) => {
    const sanitized = sanitizePrix(val);
    marquerSaisie();
    setPrixLignes((prev) => ({
      ...prev,
      [articleId]: { ...(prev[articleId] ?? PRIX_LIGNE_VIDE), [champ]: sanitized },
    }));
  };

  const handleChangePrixRegion = (
    regionId: number,
    articleId: number,
    champ: keyof PrixLigne,
    val: string,
  ) => {
    const sanitized = sanitizePrix(val);
    marquerSaisie();
    setPrixParRegion((prev) => ({
      ...prev,
      [regionId]: {
        ...(prev[regionId] ?? {}),
        [articleId]: {
          ...(prev[regionId]?.[articleId] ?? PRIX_LIGNE_VIDE),
          [champ]: sanitized,
        },
      },
    }));
  };

  const handleChangePrixLot = (
    lotId: number,
    articleId: number,
    champ: keyof PrixLigne,
    val: string,
  ) => {
    const sanitized = sanitizePrix(val);
    marquerSaisie();
    setPrixParLot((prev) => ({
      ...prev,
      [lotId]: {
        ...(prev[lotId] ?? {}),
        [articleId]: {
          ...(prev[lotId]?.[articleId] ?? PRIX_LIGNE_VIDE),
          [champ]: sanitized,
        },
      },
    }));
  };

  // Ouvre la confirmation de dépôt
  const handleDeposer = () => {
    setDeposerError(null);
    setShowConfirmDeposer(true);
  };

  const handleAnnuler = () => {
    setAnnulerError(null);
    setShowConfirmAnnuler(true);
  };

  // Le back est seul juge de la fenetre d'annulation : on n'anticipe pas son
  // refus cote client, on affiche le motif qu'il renvoie.
  const handleConfirmAnnuler = () => {
    setIsAnnulling(true);
    setAnnulerError(null);
    fetch(`${apiUrl}/publications/ac27/candidatures/${id}/annuler`, {
      method: 'POST',
      headers: { ...authHeaders, 'Content-Type': 'application/json' },
    })
      .then(async (r) => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          const motif = err?.errors?.candidature?.[0] ?? err?.message ?? `Erreur ${r.status}`;
          throw new Error(motif);
        }
        return r.json();
      })
      .then(() => {
        setShowConfirmAnnuler(false);
        navigate('/publications?tab=4');
      })
      .catch((e: Error) => {
        setAnnulerError(e.message);
      })
      .finally(() => setIsAnnulling(false));
  };

  // Appelé depuis la popup après confirmation
  const handleConfirmDeposer = () => {
    setIsDeposing(true);
    setDeposerError(null);
    const payload = { representant_signature_nom: nomSignataire.trim() || undefined };
    console.log('[DEBUG 47.1] nomSignataire=', nomSignataire, 'payload=', payload);
    fetch(`${apiUrl}/publications/ac27/candidatures/${id}/deposer`, {
      method: 'POST',
      headers: { ...authHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(async (r) => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err?.message ?? `Erreur ${r.status}`);
        }
        setShowConfirmDeposer(false);
        navigate('/publications?tab=4');
      })
      .catch((e: Error) => setDeposerError(e.message))
      .finally(() => setIsDeposing(false));
  };

  // ── Rendu ────────────────────────────────────────────────────────────────────



  return (
    <div className="candidature-detail-container">
      {/* <CandidatureHeader
        accordCadreNom={candidature.accord_cadre.nom}
        acheteur={candidature.accord_cadre.acheteur}
        lotTechniqueNom={candidature.lot_technique.nom}
        lotTechniqueCpv={candidature.lot_technique.cpv}
        referenceAvis={candidature.accord_cadre.reference_avis}
        status={candidature.status}
        delais={candidature.accord_cadre.expire_at}
        onRetour={() => navigate('/publications?tab=4')}
      /> */}
      <div className='no-print'>
        <SuiviHeader
          titre={'ACCORD CADRE édition 2027-2028'}
          sousTitre={candidature.accord_cadre.nom}
          subtitle={`Candidature #${candidature.identifiant_candidature ?? candidature.id}${candidature.ref_publication ? ' · Réf. ' + candidature.ref_publication : (candidature.accord_cadre.reference_avis ? ' · Réf. ' + candidature.accord_cadre.reference_avis : '')}`}
          status={candidature.status || 'Statut inconnu'} // Assurer une valeur par défaut
          statusColor={getStatusColor(candidature.status)}
          delais={candidature.accord_cadre.date_limite_candidature ?? candidature.accord_cadre.expire_at}
          backgroundImage={lotImage}
          source="MS"
          retourLabel="Retour vers les candidatures"
          onRetour={() => navigate('/publications?tab=4')}
        />
      </div>

      {/* <TabNav
        tabs={TABS_DETAIL}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        inactiveTabs={[0, 2]}
        inactiveTabsTooltips={{
          'Avis et documents': 'Fonctionnalité en cours de développement',
          'Questions / Réponses': 'Fonctionnalité en cours de développement',
        }}
      /> */}

      <div className="suivi-de-commande-content">
        {/* Bandeau d'état d'une candidature annulée.
            Placé DANS le conteneur des sections plutôt que recopier sa largeur :
            .tab-content n'a aucun padding horizontal, donc la bannière hérite
            exactement des mêmes bornes latérales que les sections. Aucune
            constante de mise en page dupliquée, donc rien qui puisse dériver. */}
        {candidature.status === 'annulee' && (
          <Banner
            variant="danger"
            message={ligneAnnulation(candidature.annulation)}
            className="candidature-etat-banner"
          />
        )}

        {/* Brouillon hors delai. Exclusif du bandeau d'annulation ci-dessus :
            une candidature annulee a forcement ete deposee, donc n'est pas en
            brouillon. Le message vient du back, jamais redige ici — s'il manque
            (API plus ancienne), on n'invente pas de texte, on n'affiche rien ;
            le grisage du bouton, lui, tient sur depotHorsDelai seul. */}
        {depotHorsDelai && candidature.motif_refus_depot && (
          <Banner
            variant="danger"
            message={candidature.motif_refus_depot}
            className="candidature-etat-banner"
          />
        )}

        <TabNav
          tabs={TABS_DETAIL}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          disabledTabs={getDisabledTabs()}
          inactiveTabs={getInactiveTabs()}
        />

        <div className="tab-content">
          {renderTabContent()}
        </div>
      </div>

      {/* Modale CGU — CGVAccept (Story 32.4.b) */}
      {showCguModal && (
        <CGVAccept
          acceptCGV={cguModalChecked}
          setAcceptCGV={setCguModalChecked}
          isOpen={showCguModal}
          onClose={() => { setCguModalChecked(false); setShowCguModal(false); }}
          onConfirm={() => { setAcceptConditions(true); setShowCguModal(false); }}
        />
      )}

      {/* Modal inviter un membre (Story 8.5) */}
      {showInviterModal && (
        <div className="modal-backdrop" onClick={handleCloseInviterModal}>
          <div className="modal-panel inviter-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Inviter un membre au groupement</h2>
              <Button variant="ghost" value="×" onClick={handleCloseInviterModal} />
            </div>
            <div className="modal-body">
              {inviteError && (
                <div className="inviter-error">{inviteError}</div>
              )}

              <label className="inviter-section-label">Rechercher un prestataire</label>
              <div className="inviter-search-wrapper">
                <input
                  type="text"
                  className="inviter-search-input"
                  placeholder="Rechercher par nom ou SIRET…"
                  value={inviteSearchQuery}
                  onChange={(e) => handleInviteSearchChange(e.target.value)}
                  autoComplete="off"
                  autoFocus
                />
                {inviteIsSearching && <span className="inviter-search-spinner" />}

                {inviteSearchResults.length > 0 && (
                  <div className="inviter-search-results">
                    {inviteSearchResults.map((entite) => (
                      <button
                        key={entite.id}
                        type="button"
                        className="inviter-search-result-item"
                        onClick={() => handleInviteAddMembre(entite)}
                      >
                        <span className="inviter-result-nom">
                          {entite.nom}{entite.siret ? ` (SIRET : ${entite.siret})` : ''}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {inviteSearchQuery.trim().length >= 2 && !inviteIsSearching && inviteSearchResults.length === 0 && (
                  <div className="inviter-search-empty">Aucun résultat.</div>
                )}
              </div>

              {inviteMembres.length > 0 && (
                <div className="inviter-membres-list">
                  <p className="inviter-membres-count">
                    {inviteMembres.length} membre{inviteMembres.length > 1 ? 's' : ''} à inviter
                  </p>
                  {inviteMembres.map((m) => (
                    <div key={m.id} className="inviter-membre-row">
                      <span className="inviter-membre-nom">
                        {m.nom}{m.siret ? ` (SIRET : ${m.siret})` : ''}
                      </span>
                      <button
                        type="button"
                        className="inviter-membre-remove"
                        onClick={() => handleInviteRemoveMembre(m.id)}
                        aria-label={`Retirer ${m.nom}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <Button
                variant="secondary"
                value="Annuler"
                onClick={handleCloseInviterModal}
                disabled={inviteSubmitting}
              />
              <Button
                variant="primary"
                value={inviteSubmitting ? 'Envoi…' : `Inviter${inviteMembres.length > 0 ? ` (${inviteMembres.length})` : ''}`}
                onClick={handleInviteSubmit}
                disabled={inviteMembres.length === 0 || inviteSubmitting}
              />
            </div>
          </div>
        </div>
      )}
      {/* Popup confirmation dépôt définitif */}
      <ValidationPopUp
        isOpen={showConfirmDeposer}
        title="Confirmer le dépôt"
        text="Cette action est irréversible. Le dossier ne pourra plus être modifié. Un bordereau d'accusé de réception vous sera envoyé par email à l'issue."
        confirmLabel="Déposer mon offre"
        cancelLabel="Annuler"
        isLoading={isDeposing}
        onConfirm={handleConfirmDeposer}
        onClose={() => { setShowConfirmDeposer(false); setDeposerError(null); }}
      >
        {deposerError && (
          <p style={{ color: '#b91c1c', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '0.375rem', padding: '0.6rem 1rem', margin: '0.5rem 0 0', fontSize: '1.2rem' }}>
            ✗ {deposerError}
          </p>
        )}
      </ValidationPopUp>

      {/* Popup confirmation toggle prix identiques/différents (Story 13.5) */}
      <ValidationPopUp
        isOpen={showTogglePrixConfirm}
        title="Changer le mode de prix"
        text={isRegional
          ? `Attention : les prix de la première région (${regionGroups[0]?.regionNom ?? 'première région'}) seront appliqués à toutes les autres régions. Les prix différents que vous avez saisis seront écrasés.`
          : `Attention : les prix du premier département seront appliqués à tous les autres départements. Les prix différents que vous avez saisis seront écrasés.`
        }
        confirmLabel="Confirmer le changement"
        cancelLabel="Annuler"
        onConfirm={() => {
          marquerSaisie();
          setPrixIdentiques(true);
          if (isRegional) {
            const firstGroup = regionGroups[0];
            if (firstGroup && prixParRegion[firstGroup.regionId]) {
              setPrixLignes({ ...prixParRegion[firstGroup.regionId] });
            }
          } else {
            const firstLotId = lotsGeoIds[0];
            if (firstLotId && prixParLot[firstLotId]) {
              setPrixLignes({ ...prixParLot[firstLotId] });
            }
          }
          setShowTogglePrixConfirm(false);
        }}
        onClose={() => setShowTogglePrixConfirm(false)}
      />

      {/*
        Barre d'actions fixe en bas (Story 13.7).
        Rendue en brouillon comme avant, ET sur une candidature deposee encore
        annulable : c'est le seul endroit ou le prestataire peut agir, et
        l'annulation est justement interdite en brouillon.
        Hors brouillon la barre ne porte que "Retour" et "Annuler ma candidature" :
        pas de flush d'autosave, pas de depot, pas d'indicateur d'enregistrement.

        canDeposer commence par !depotHorsDelai : passe l'echeance, "Deposer mon
        offre" reste grise meme dossier complet et case cochee. Le back refuserait
        de toute facon, mais griser evite d'envoyer le prestataire dans une erreur
        apres lui avoir laisse croire que le depot etait ouvert.
      */}
      {(candidature.status === 'brouillon' || (estMandataire && candidature.peut_annuler === true)) && (
        <BottomActionBar
          isBrouillon={candidature.status === 'brouillon'}
          onQuitter={candidature.status === 'brouillon' ? handleQuitter : () => navigate('/publications?tab=4')}
          onDeposer={handleDeposer}
          canDeposer={!depotHorsDelai && estMandataire && acceptConditions && !!pdfUrl && validationItems.every((item) => item.ok === true) && !isDeposing}
          estMandataire={estMandataire}
          saveStatus={estMandataire && candidature.status === 'brouillon' ? globalSaveStatus : 'idle'}
          saveError={estMandataire && candidature.status === 'brouillon' ? globalSaveError : null}
          onPreviewPdf={candidature.status === 'brouillon' ? handlePreviewPdf : undefined}
          isGeneratingPdf={isGeneratingPdf}
          canPreviewPdf={acceptConditions && validationItems.every((item) => item.ok === true)}
          onAnnuler={handleAnnuler}
          canAnnuler={estMandataire && candidature.peut_annuler === true}
          isAnnulling={isAnnulling}
        />
      )}

      {/* Popup confirmation retrait membre */}
      <ValidationPopUp
        isOpen={showRetirerModal}
        title="Retirer un membre du groupement"
        text={`Êtes-vous sûr de vouloir retirer ${retirerTarget?.nom ?? 'ce membre'} du groupement ? Cette action est irréversible.`}
        onClose={() => { setShowRetirerModal(false); setRetirerTarget(null); }}
        onConfirm={confirmRetirerMembre}
        cancelLabel="Annuler"
        confirmLabel="Retirer"
        isLoading={retirerLoading}
      />

      {/* Popup confirmation annulation de la candidature */}
      <ValidationPopUp
        isOpen={showConfirmAnnuler}
        title={titreAnnulationCandidature(numeroCandidature(candidature))}
        text={
          annulerError
            ? `Annulation impossible : ${annulerError}`
            : texteAnnulationCandidature(
                numeroCandidature(candidature),
                `${candidature.lot_technique.code} — ${candidature.lot_technique.nom}`,
              )
        }
        onClose={() => { setShowConfirmAnnuler(false); setAnnulerError(null); }}
        onConfirm={handleConfirmAnnuler}
        cancelLabel="Revenir en arrière"
        confirmLabel="Annuler définitivement"
        confirmVariant="danger"
        isLoading={isAnnulling}
      />

      {/* Story 8.12 — Modale profil membre groupement (lecture seule) */}
      <ModalProfilMembre
        isOpen={showMemberProfileModal}
        onClose={() => { setShowMemberProfileModal(false); setMemberProfile(null); setMemberProfileError(null); }}
        profile={memberProfile}
        isLoading={memberProfileLoading}
        error={memberProfileError}
      />

      {/* Modale preview convention signée */}
      {conventionPreviewBlobUrl && (
        <div className="modal-backdrop" onClick={handleCloseConventionPreview}>
          <div className="convention-preview-panel" onClick={(e) => e.stopPropagation()}>
            <div className="convention-preview-header">
              <span className="convention-preview-title">{conventionPreviewFilename}</span>
              <Button variant="ghost" value="✕" onClick={handleCloseConventionPreview} />
            </div>
            <iframe
              src={conventionPreviewBlobUrl}
              title={conventionPreviewFilename}
              className="convention-preview-iframe"
            />
            <div className="convention-preview-actions">
              <Button
                type="button"
                value="Télécharger"
                size="small"
                onClick={handleDownloadConventionFromPreview}
              />
              <Button
                type="button"
                value="Fermer"
                size="small"
                variant="secondary"
                onClick={handleCloseConventionPreview}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DetailCandidature;
